import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './HomePage.module.css';
import {
  createOpenPayCardToken,
  setupOpenPayDeviceSession,
} from '../../utils/openpay';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CAREER_TYPE_LABELS = {
  bachillerato: 'Bachillerato',
  tsu: 'TSU',
  degree: 'Licenciatura',
  master: 'Maestría',
  masterDegree: 'Maestría',
  speciality: 'Especialidad',
};

const BRAND_GRADIENTS = {
  visa: 'linear-gradient(135deg, #1A1F71 0%, #2e3aaa 100%)',
  mastercard: 'linear-gradient(135deg, #EB001B 0%, #c23a00 100%)',
  amex: 'linear-gradient(135deg, #007BC1 0%, #005a8e 100%)',
  american_express: 'linear-gradient(135deg, #007BC1 0%, #005a8e 100%)',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatPaymentDate(dateStr) {
  if (!dateStr) return 'Por definir';
  return new Date(dateStr).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatCurrency(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
}

function CardChip() {
  return (
    <svg width="34" height="26" viewBox="0 0 34 26" fill="none" aria-hidden="true">
      <rect width="34" height="26" rx="3" fill="#D4AF37" opacity="0.85" />
      <rect x="1" y="8" width="32" height="9" fill="#c9a227" opacity="0.45" />
      <rect x="12" y="1" width="10" height="24" fill="#c9a227" opacity="0.45" />
    </svg>
  );
}

const emptyCard = () => ({
  holderName: '',
  cardNumber: '',
  expirationMonth: '',
  expirationYear: '',
  cvv2: '',
});

function PaymentCard({ method, deleting, onDelete }) {
  return (
    <div className={styles.paymentMethodItem}>
      <div
        className={styles.cardMockup}
        style={{
          background:
            BRAND_GRADIENTS[method.brand] ||
            'linear-gradient(135deg, #1e1140 0%, #3b2280 100%)',
        }}
      >
        <div className={styles.mockupTop}>
          <CardChip />
          <span className={styles.mockupBrand}>{method.brand?.toUpperCase() || ''}</span>
        </div>
        <div className={styles.mockupNumber}>
          •••• &nbsp;•••• &nbsp;•••• &nbsp;{method.last4}
        </div>
        <div className={styles.mockupBottom}>
          <div>
            <div className={styles.mockupLabel}>TITULAR</div>
            <div className={styles.mockupValue}>{method.holderName}</div>
          </div>
          <div>
            <div className={styles.mockupLabel}>VENCE</div>
            <div className={styles.mockupValue}>
              {method.expirationMonth}/{method.expirationYear}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.paymentMethodFooter}>
        <div>
          <strong>{method.bankName || 'Tarjeta bancaria'}</strong>
          <span>{method.type === 'debit' ? 'Débito' : 'Crédito'}</span>
        </div>
        {method.hasSubscription && (
          <span className={styles.subscriptionBadge}>Suscripción activa</span>
        )}
        <button
          type="button"
          className={styles.deleteCardButton}
          disabled={deleting}
          onClick={() => onDelete(method)}
        >
          {deleting ? 'Eliminando…' : 'Eliminar'}
        </button>
      </div>
    </div>
  );
}

function LoadingPage() {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <img
          src="/logos/logos_4.png"
          className={styles.headerLogo}
          alt="ODM"
          onError={(e) => { e.target.src = '/logos/logos_1.png'; }}
        />
        <span className={styles.headerTitle}>Portal Académico</span>
      </header>
      <div className={styles.stateBox}>Cargando tu perfil…</div>
    </div>
  );
}

function ErrorPage({ message, onLogout }) {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <img
          src="/logos/logos_4.png"
          className={styles.headerLogo}
          alt="ODM"
          onError={(e) => { e.target.src = '/logos/logos_1.png'; }}
        />
        <span className={styles.headerTitle}>Portal Académico</span>
        <button type="button" className={styles.logoutBtn} onClick={onLogout}>
          Cerrar sesión
        </button>
      </header>
      <div className={styles.stateBox} data-error="true">{message}</div>
    </div>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cardFormOpen, setCardFormOpen] = useState(false);
  const [card, setCard] = useState(emptyCard);
  const [moveSubscription, setMoveSubscription] = useState(true);
  const [cardActionError, setCardActionError] = useState('');
  const [cardActionMessage, setCardActionMessage] = useState('');
  const [savingCard, setSavingCard] = useState(false);
  const [deletingCardId, setDeletingCardId] = useState('');
  const [openpayReady, setOpenpayReady] = useState(false);
  const deviceSessionIdRef = useRef('');

  const handleLogout = () => {
    localStorage.removeItem('odm_token');
    localStorage.removeItem('odm_user_id');
    localStorage.removeItem('odm_role');
    navigate('/portal');
  };

  useEffect(() => {
    const token = localStorage.getItem('odm_token');
    if (!token) { navigate('/portal'); return; }

    fetch(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => {
        if (!json.success) throw new Error(json.message || 'Error al cargar el perfil.');
        setData(json.data);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (!cardFormOpen) return undefined;
    let cancelled = false;
    setOpenpayReady(false);
    setCardActionError('');

    setupOpenPayDeviceSession('home-openpay-form', 'homeDeviceSessionId')
      .then((deviceSessionId) => {
        if (cancelled) return;
        deviceSessionIdRef.current = deviceSessionId
          || document.getElementById('homeDeviceSessionId')?.value
          || '';
        setOpenpayReady(Boolean(deviceSessionIdRef.current));
      })
      .catch(() => {
        if (!cancelled) {
          setCardActionError('No se pudo inicializar Openpay. Recarga la página.');
        }
      });

    return () => { cancelled = true; };
  }, [cardFormOpen]);

  const updatePaymentState = (response) => {
    setData((current) => ({
      ...current,
      paymentMethods: response.data,
      maxPaymentMethods: response.maxPaymentMethods,
      subscription: response.subscription,
    }));
  };

  const handleCardChange = (event) => {
    const { name, value } = event.target;
    setCard((current) => ({ ...current, [name]: value }));
  };

  const handleAddCard = async (event) => {
    event.preventDefault();
    setCardActionError('');
    setCardActionMessage('');

    if (!Object.values(card).every((value) => value.trim())) {
      setCardActionError('Completa todos los datos de la tarjeta.');
      return;
    }
    if (!openpayReady || !deviceSessionIdRef.current) {
      setCardActionError('Openpay todavía se está inicializando. Intenta nuevamente.');
      return;
    }

    setSavingCard(true);
    try {
      const tokenId = await createOpenPayCardToken(card);
      const token = localStorage.getItem('odm_token');
      const response = await fetch(`${API_URL}/payment/methods`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tokenId,
          deviceSessionId: deviceSessionIdRef.current,
          moveSubscription,
        }),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || 'No se pudo agregar la tarjeta.');

      updatePaymentState(json);
      setCard(emptyCard());
      setCardFormOpen(false);
      setCardActionMessage(
        json.movedSubscription
          ? `Tarjeta agregada y suscripción programada en ${json.trialDays} días.`
          : 'Tarjeta agregada correctamente.'
      );
    } catch (operationError) {
      setCardActionError(operationError.message);
    } finally {
      setSavingCard(false);
    }
  };

  const handleDeleteCard = async (method) => {
    const warning = method.hasSubscription
      ? 'Esta tarjeta tiene la suscripción activa. Al eliminarla también se cancelará la suscripción. ¿Deseas continuar?'
      : '¿Deseas eliminar esta tarjeta?';
    if (!window.confirm(warning)) return;

    setDeletingCardId(method.openpayCardId);
    setCardActionError('');
    setCardActionMessage('');
    try {
      const token = localStorage.getItem('odm_token');
      const response = await fetch(
        `${API_URL}/payment/methods/${method.openpayCardId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || 'No se pudo eliminar la tarjeta.');

      updatePaymentState(json);
      setCardActionMessage(
        json.removedSubscription
          ? 'Tarjeta eliminada y suscripción cancelada. Agrega otra tarjeta para reactivarla.'
          : 'Tarjeta eliminada correctamente.'
      );
    } catch (operationError) {
      setCardActionError(operationError.message);
    } finally {
      setDeletingCardId('');
    }
  };

  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage message={error} onLogout={handleLogout} />;

  const {
    profile,
    enrollment,
    payments,
    paymentSummary,
    paymentMethods = [],
    maxPaymentMethods = 3,
    subscription = {},
  } = data;
  const initials = `${profile.firstName[0]}${profile.lastName[0]}`.toUpperCase();
  const progressPct =
    paymentSummary.total > 0
      ? Math.round((paymentSummary.paid / paymentSummary.total) * 100)
      : 0;

  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <header className={styles.header}>
        <img
          src="/logos/logos_4.png"
          className={styles.headerLogo}
          alt="ODM"
          onError={(e) => { e.target.src = '/logos/logos_1.png'; }}
        />
        <span className={styles.headerTitle}>Portal Académico</span>
        <span className={styles.headerName}>
          {profile.firstName} {profile.lastName}
        </span>
        <button type="button" className={styles.logoutBtn} onClick={handleLogout}>
          Cerrar sesión
        </button>
      </header>

      <div className={styles.content}>
        {/* ── Row 1: Perfil + Carrera ── */}
        <div className={styles.topRow}>
          {/* Perfil */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Mi perfil</h2>
            <div className={styles.profileInner}>
              <div className={styles.avatar}>{initials}</div>
              <div className={styles.fields}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Nombre completo</span>
                  <span className={styles.fieldValue}>
                    {profile.firstName} {profile.lastName}
                    {profile.motherLastName ? ` ${profile.motherLastName}` : ''}
                  </span>
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Correo electrónico</span>
                  <span className={styles.fieldValue}>{profile.email}</span>
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Teléfono</span>
                  <span className={styles.fieldValue}>{profile.phone}</span>
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Fecha de nacimiento</span>
                  <span className={styles.fieldValue}>{formatDate(profile.birthDate)}</span>
                </div>
              </div>
            </div>
            <div className={styles.nextPaymentDate}>
              <span className={styles.nextPaymentLabel}>Siguiente fecha de pago</span>
              <strong className={styles.nextPaymentValue}>
                {formatPaymentDate(enrollment.nextPaymentDate)}
              </strong>
            </div>
          </div>

          {/* Carrera */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Mi carrera</h2>
            <div className={styles.careerInner}>
              {enrollment.careerType && (
                <span className={styles.typeBadge}>
                  {CAREER_TYPE_LABELS[enrollment.careerType] || enrollment.careerType}
                </span>
              )}
              <p className={styles.careerName}>{enrollment.careerName}</p>

              <div className={styles.fields}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Plan de pago</span>
                  <span className={styles.fieldValue}>{enrollment.planLabel}</span>
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Colegiatura mensual</span>
                  <span className={styles.fieldValue}>
                    {formatCurrency(enrollment.planTotal)}
                    {enrollment.planDiscount > 0 && (
                      <span className={styles.discountBadge}>{enrollment.planDiscount}% beca</span>
                    )}
                  </span>
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Duración del programa</span>
                  <span className={styles.fieldValue}>
                    {enrollment.careerDurationMonths} mensualidades
                  </span>
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Inscrito desde</span>
                  <span className={styles.fieldValue}>{formatDate(enrollment.enrolledAt)}</span>
                </div>
              </div>

              {/* Progress bar */}
              {paymentSummary.total > 0 && (
                <div className={styles.progressSection}>
                  <div className={styles.progressHeader}>
                    <span className={styles.progressLabel}>Avance de pagos</span>
                    <span className={styles.progressPct}>{progressPct}%</span>
                  </div>
                  <div className={styles.progressTrack}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className={styles.progressLegend}>
                    <span className={styles.legendPaidText}>
                      {paymentSummary.paid} pagadas
                    </span>
                    <span className={styles.legendPendingText}>
                      {paymentSummary.pending} pendientes
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Row 2: Mensualidades ── */}
        {paymentSummary.total > 0 && (
          <div className={styles.card}>
            <div className={styles.cardTitleRow}>
              <h2 className={styles.cardTitle}>Mis mensualidades</h2>
              <div className={styles.mensLegend}>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendDot} ${styles.dotPaid}`} />
                  Pagada
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendDot} ${styles.dotPending}`} />
                  Pendiente
                </span>
              </div>
            </div>
            <div className={styles.mensGrid}>
              {Array.from({ length: paymentSummary.total }, (_, i) => {
                const month = i + 1;
                const paidEntry = payments.find((p) => p.monthNumber === month);
                return (
                  <div
                    key={month}
                    className={`${styles.pill} ${paidEntry ? styles.pillPaid : styles.pillPending}`}
                    title={paidEntry ? `Pagada el ${formatDate(paidEntry.paidAt)}` : `Mensualidad #${month} pendiente`}
                  >
                    <span className={styles.pillNum}>{month}</span>
                    {paidEntry && (
                      <span className={styles.pillDate}>{formatDate(paidEntry.paidAt)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Row 3: Métodos de pago ── */}
        <div className={styles.card}>
          <div className={styles.cardTitleRow}>
            <h2 className={styles.cardTitle}>Mis tarjetas</h2>
            <span className={styles.cardCount}>{paymentMethods.length}/{maxPaymentMethods}</span>
            <button
              type="button"
              className={styles.addCardButton}
              disabled={paymentMethods.length >= maxPaymentMethods}
              onClick={() => {
                setCardFormOpen((open) => !open);
                setCardActionError('');
                setCardActionMessage('');
              }}
            >
              {cardFormOpen ? 'Cancelar' : 'Agregar tarjeta'}
            </button>
          </div>

          {cardActionError && <p className={styles.cardActionError}>{cardActionError}</p>}
          {cardActionMessage && <p className={styles.cardActionSuccess}>{cardActionMessage}</p>}

          {paymentMethods.length ? (
            <div className={styles.paymentMethodsGrid}>
              {paymentMethods.map((method) => (
                <PaymentCard
                  key={method.openpayCardId}
                  method={method}
                  deleting={deletingCardId === method.openpayCardId}
                  onDelete={handleDeleteCard}
                />
              ))}
            </div>
          ) : (
            <div className={styles.noCards}>No tienes tarjetas registradas.</div>
          )}

          {enrollment.paymentMode === 'preferente' && !subscription.hasActiveSubscription && (
            <p className={styles.subscriptionNotice}>
              Tu beca preferente no tiene una suscripción activa. Al agregar una tarjeta se
              programará automáticamente usando tu plan original y tu siguiente fecha de pago.
            </p>
          )}

          {cardFormOpen && (
            <form id="home-openpay-form" className={styles.cardForm} onSubmit={handleAddCard}>
              <input type="hidden" id="homeDeviceSessionId" name="homeDeviceSessionId" />
              <h3>Agregar nueva tarjeta</h3>
              <div className={styles.cardLogos}>
                <img src="/openpay/visa.png" alt="Visa" />
                <img src="/openpay/masterCard.png" alt="Mastercard" />
                <img src="/openpay/americanExpress.png" alt="American Express" />
              </div>
              <div className={styles.cardFormGrid}>
                <label className={styles.formField}>
                  <span>Número de tarjeta</span>
                  <input
                    name="cardNumber"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    maxLength={19}
                    placeholder="1234 5678 9012 3456"
                    value={card.cardNumber}
                    onChange={(event) => {
                      const raw = event.target.value.replace(/\D/g, '').slice(0, 16);
                      const formatted = raw.replace(/(.{4})/g, '$1 ').trim();
                      setCard((current) => ({ ...current, cardNumber: formatted }));
                    }}
                  />
                </label>
                <label className={styles.formField}>
                  <span>Nombre del titular</span>
                  <input
                    name="holderName"
                    autoComplete="cc-name"
                    value={card.holderName}
                    onChange={handleCardChange}
                  />
                </label>
                <label className={styles.formField}>
                  <span>Mes</span>
                  <select
                    name="expirationMonth"
                    value={card.expirationMonth}
                    onChange={handleCardChange}
                  >
                    <option value="">MM</option>
                    {Array.from({ length: 12 }, (_, index) => {
                      const month = String(index + 1).padStart(2, '0');
                      return <option key={month} value={month}>{month}</option>;
                    })}
                  </select>
                </label>
                <label className={styles.formField}>
                  <span>Año</span>
                  <select
                    name="expirationYear"
                    value={card.expirationYear}
                    onChange={handleCardChange}
                  >
                    <option value="">AA</option>
                    {Array.from({ length: 12 }, (_, index) => {
                      const year = String(new Date().getFullYear() + index).slice(-2);
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </label>
                <label className={styles.formField}>
                  <span>CVV</span>
                  <input
                    name="cvv2"
                    type="password"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    maxLength={4}
                    value={card.cvv2}
                    onChange={(event) => setCard((current) => ({
                      ...current,
                      cvv2: event.target.value.replace(/\D/g, '').slice(0, 4),
                    }))}
                  />
                </label>
              </div>

              {enrollment.paymentMode === 'preferente' && subscription.hasActiveSubscription && (
                <label className={styles.moveSubscriptionOption}>
                  <input
                    type="checkbox"
                    checked={moveSubscription}
                    onChange={(event) => setMoveSubscription(event.target.checked)}
                  />
                  <span>Mover mi suscripción activa a esta nueva tarjeta.</span>
                </label>
              )}

              <div className={styles.cardFormActions}>
                <span>Los datos sensibles se envían directamente a Openpay.</span>
                <button type="submit" disabled={savingCard || !openpayReady}>
                  {savingCard ? 'Guardando…' : 'Guardar tarjeta'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default HomePage;
